import { CookieMonster } from 'root/utils/cookies/cookieMonster';
import Loader from 'root/main/components/loader/loader';
import React from 'react';
import ReactDOM from 'react-dom';
import { LoggingHandler } from 'root/utils/errorReporting/LoggingHandler';

const exclusionList: { [key: string]: string[] } = {
    retail: [
        'demo-thank-you-2',
        'contact-thank-you',
        'talk-to-a-pos-expert',
        'register-for-a-free-trial',
        'payments/talk-to-an-expert',
        'watch-a-demo',
        'contact-us',
    ],
    restaurant: [
        'demo-thank-you-2',
        'contact-thank-you',
        'login',
        'talk-to-a-pos-expert',
        'register-for-a-free-trial-sales',
        'watch-a-demo',
        'free-menu-templates',
    ],
};

const cm = new CookieMonster();

const showWaitingSpinner = (): void => {
    if (document.querySelector('.vwo-spinner-container')) return;

    const siteContent = document.querySelector('#page');
    if (siteContent && !siteContent.classList.contains('tw:blur-[5px]'))
        siteContent.classList.add('tw:blur-[5px]');

    const spinnerContainer = document.createElement('div');
    spinnerContainer.className =
        'vwo-spinner-container tw:fixed tw:top-0 tw:left-0 tw:w-screen tw:h-screen tw:z-[1100] tw:overflow-y-scroll tw:overflow-x-hidden';
    ReactDOM.render(React.createElement(Loader), spinnerContainer);
    document.body.insertBefore(spinnerContainer, document.body.firstChild);
};

const hideWaitingSpinner = (removeBlur: boolean | null = true): void => {
    const spinner = document.querySelector('.vwo-spinner-container');
    const siteContent = document.querySelector('#page');
    if (siteContent && removeBlur) siteContent.classList.remove('tw:blur-[5px]');
    if (spinner) document.body.removeChild(spinner);
};

const getVWOPageProduct = (): string => {
    const url = new URL(window.location.href);
    return url.pathname.includes('/pos/retail/')
        ? 'retail'
        : url.pathname.includes('/pos/restaurant/')
        ? 'restaurant'
        : 'skip';
};

const getCookieByPartialName = (baseName: string): number | null => {
    const cookies = document.cookie.split(';').map(c => c.trim());
    for (const cookie of cookies) {
        const [name, value] = cookie.split('=');
        if (name.includes(baseName)) {
            return value ? parseInt(value) : null;
        }
    }
    return null;
};

const getVWOCampaignName = (): string => {
    const vertical = getVWOPageProduct();
    const campaignCookieName: { [key: string]: string } = {
        retail: '_vis_opt_exp_108_combi',
        restaurant: '_vis_opt_exp_109_combi',
        skip: '',
    };
    return campaignCookieName[vertical];
};

const isOneTrustBlockingCookies = (): boolean => {
    const OneTrustConsentCookie = cm.getCookie('OptanonConsent');
    // intType=2 is the indicator that a user selected to refuse all cookies
    return OneTrustConsentCookie ? OneTrustConsentCookie.includes('intType=2') : false;
};

const isCOMDomainAndNotQuebec = (): boolean => {
    const url = new URL(window.location.href);
    const geoCookie = cm.getCookie('geoConfig');

    const patterns = ['com.au', '/ch/', '/ch-de/', 'fr.lightspeedhq.com'];
    const matches = patterns.some(pattern => url.origin.includes(pattern));

    return matches || geoCookie.state_code === 'QC' ? false : url.origin.includes('com');
};

async function waitForVWOCampaign(timeout = 3000, interval = 100): Promise<boolean> {
    const url = new URL(window.location.href);
    if (isOneTrustBlockingCookies() || !isCOMDomainAndNotQuebec()) return false;

    const product = getVWOPageProduct();
    const campaignCookieName = getVWOCampaignName();

    if (product && product === 'skip') return false;
    if (exclusionList[product].some(exclusion => url.pathname.includes(exclusion)))
        return false;

    return new Promise(resolve => {
        const startTime = Date.now();
        function recursiveCheck() {
            // the reason we didn't use cm.getCookie here is because sometimes the cookie name will have a suffix (e.g. _vis_opt_exp_111_combi_choose instead of _vis_opt_exp_111_combi)
            // the getCookieByPartialName function will return the value of the cookie if it finds the cookie with the base name (with or without suffix)
            const campaignVariation = getCookieByPartialName(campaignCookieName);
            if (campaignVariation) {
                hideWaitingSpinner();
                resolve(true);
            } else if (Date.now() - startTime > timeout) {
                hideWaitingSpinner();
                LoggingHandler().logError('VWO cookie search function timed out', {
                    searchTerm: 'vwo-cookie-timeout',
                    fileName: 'waitForVWOCookie.ts',
                    tags: ['cookies'],
                    additionalData: {
                        url: window.location.href,
                        product,
                        campaignCookieName,
                        campaignVariation,
                        cookies: document.cookie,
                    },
                });
                resolve(false);
            } else {
                showWaitingSpinner();
                setTimeout(recursiveCheck, interval);
            }
        }
        recursiveCheck();
    });
}

export {
    waitForVWOCampaign,
    getVWOCampaignName,
    getCookieByPartialName,
    showWaitingSpinner,
    hideWaitingSpinner,
};
